/**
 * Modal that asks which orgs should receive the pending auth-letter save.
 *
 * Shown before toggle / upload / add-payer writes so the same field patch can
 * fan out to every selected org's `payer_prompt` doc. The org list is supplied
 * by the parent (typically every healthcare facility).
 * @module SaveToOrgsModal
 */
import { useEffect, useMemo, useState } from "react";

/** One selectable organization in the save-to-orgs modal. */
export interface SaveToOrgOption {
  id: string;
  label: string;
}

interface Props {
  /** Short label describing what is being saved (shown in the subtitle). */
  summary: string;
  /** Optional filename chip shown for upload flows. */
  fileName?: string;
  /** Healthcare facilities / orgs available to save to. */
  organizations: SaveToOrgOption[];
  /** `true` while the facility list is still loading. */
  loading?: boolean;
  /** Org IDs pre-checked when the modal opens. */
  initialSelectedOrgIds?: string[];
  onCancel: () => void;
  /** Called with the selected org IDs when the user confirms. */
  onConfirm: (orgIds: string[]) => void;
}

/**
 * Renders checkboxes for the given organizations and returns the selection.
 *
 * @param props - Modal props including org list and confirm/cancel handlers
 * @return The save-to-orgs modal overlay
 */
const SaveToOrgsModal = ({
  summary,
  fileName,
  organizations,
  loading = false,
  initialSelectedOrgIds,
  onCancel,
  onConfirm,
}: Props) => {
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(initialSelectedOrgIds ?? []),
  );
  const [filter, setFilter] = useState("");
  const [error, setError] = useState<string | null>(null);
  // Enter animation — start slightly faded so the modal eases in after the
  // native file picker (or prior dialog) has finished dismissing.
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  const visibleOrgs = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return organizations;
    return organizations.filter(
      (o) =>
        o.label.toLowerCase().includes(q) || o.id.toLowerCase().includes(q),
    );
  }, [organizations, filter]);

  const allVisibleSelected =
    visibleOrgs.length > 0 && visibleOrgs.every((o) => selected.has(o.id));

  /**
   * Toggles one org in or out of the selection set.
   *
   * @param orgId - Org whose checkbox was clicked
   */
  const toggleOrg = (orgId: string) => {
    setError(null);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(orgId)) next.delete(orgId);
      else next.add(orgId);
      return next;
    });
  };

  /** Selects or clears every currently visible org in one click. */
  const toggleAll = () => {
    setError(null);
    const visibleIds = visibleOrgs.map((o) => o.id);
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };

  /** Validates selection and forwards org IDs to the parent. */
  const submit = () => {
    if (selected.size === 0) {
      setError("Select at least one organization.");
      return;
    }
    onConfirm(Array.from(selected));
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center transition-colors duration-200 ${
        entered ? "bg-black/40" : "bg-black/0"
      }`}
      onClick={onCancel}
    >
      <div
        className={`w-[360px] max-w-[92vw] rounded-lg bg-white p-4 shadow-xl transition-all duration-200 ease-out ${
          entered
            ? "translate-y-0 scale-100 opacity-100"
            : "translate-y-2 scale-95 opacity-0"
        }`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="save-to-orgs-title"
      >
        <div
          id="save-to-orgs-title"
          className="text-base font-bold text-primaryGray-1"
        >
          Save to organizations
        </div>
        <p className="mt-1 text-sm text-primaryGray-6">{summary}</p>

        {fileName && (
          <div className="mt-3 flex items-center gap-2 rounded border border-primaryGray-14 bg-primaryGray-16 px-3 py-2">
            <span className="text-tiny font-semibold uppercase tracking-wide text-primaryGray-6">
              File
            </span>
            <span
              className="truncate text-sm font-semibold text-primaryGray-1"
              title={fileName}
            >
              {fileName}
            </span>
          </div>
        )}

        <div className="mt-4 flex items-center justify-between">
          <span className="text-xs font-semibold text-primaryGray-1">
            Organizations
          </span>
          <button
            type="button"
            onClick={toggleAll}
            disabled={loading || visibleOrgs.length === 0}
            className="text-tiny font-semibold text-primaryGray-1 underline hover:text-primaryGray-6 disabled:cursor-not-allowed disabled:no-underline disabled:opacity-50"
          >
            {allVisibleSelected ? "Clear all" : "Select all"}
          </button>
        </div>

        <input
          className="mt-2 w-full rounded border border-primaryGray-14 bg-white px-3 py-2 text-sm text-primaryGray-1 focus:border-primaryGray-1 focus:outline-none"
          placeholder="Search organizations"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          disabled={loading}
          autoFocus
        />

        <div className="mt-2 flex h-auto max-h-48 flex-col gap-2 overflow-y-auto rounded border border-primaryGray-14 p-3">
          {loading ? (
            <div className="py-4 text-center text-sm text-primaryGray-6">
              Loading organizations…
            </div>
          ) : visibleOrgs.length === 0 ? (
            <div className="py-4 text-center text-sm text-primaryGray-6">
              {organizations.length === 0
                ? "No organizations found."
                : "No organizations match your search."}
            </div>
          ) : (
            visibleOrgs.map((org) => {
              const checked = selected.has(org.id);
              return (
                <label
                  key={org.id}
                  className="flex cursor-pointer items-center gap-2 text-sm text-primaryGray-1"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleOrg(org.id)}
                    className="h-4 w-4 shrink-0 accent-gray-800"
                  />
                  <span className="font-semibold">{org.label}</span>
                </label>
              );
            })
          )}
        </div>

        {error && <div className="mt-3 text-xs text-red-500">{error}</div>}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-primaryGray-14 px-3 py-2 text-tiny font-semibold text-primaryGray-1 hover:bg-primaryGray-16"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={loading}
            className="rounded border border-gray-800 bg-gray-800 px-4 py-2 text-tiny font-semibold text-white hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Save to {selected.size || "…"} org
            {selected.size === 1 ? "" : "s"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaveToOrgsModal;
